<template>
  <div>
    <h1 class="common__main-title">Work summary update</h1>
    <div class="common__sub-title">Update</div>

    <div v-if="!isConfirmed">
      <div class="common__main-contener mb-4">
        <form class="common__main-content">
          <div class="form-group">
            <label>Year</label>
            <select class="form-control" v-model="selectedYear">
              <option v-for="year of years" :key="year" :value="year">{{ year }}</option>
            </select>
          </div>
          <div class="form-group mb-5">
            <label>Month</label>
            <select class="form-control" v-model="selectedMonth">
              <option v-for="{ month, label} of months" :key="month" :value="month">
                {{ label }}
              </option>
            </select>
          </div>
          <div class="d-flex justify-content-center">
            <b-button pill variant="primary" @click="download">
              Download
            </b-button>
          </div>
        </form>
      </div>

      <div class="common__main-contener">
        <form class="common__main-content">
          <div class="form-group">
            <label>Work summary csv file</label>
            <FileInput
              v-model="selectedCsv"
              ref="csv"
              @changedFile="error = null"
            />
            <div class="main-content__msg-box">
              <ErrorCsv :errorMessages="error" />
            </div>
          </div>
          <div class="d-flex justify-content-end">
            <b-button pill variant="primary" @click="confirmUploadCsv">
              Confirm
            </b-button>
          </div>
        </form>
      </div>
    </div>
    <div v-else>
      <div class="common__main-contener">
        <TimesheetWorkSummaryTable :diffs="diffs"/>
        <div class="d-flex justify-content-between">
          <b-button pill variant="secondary" @click="isConfirmed = false" >
            Back
          </b-button>
          <b-button
            pill
            variant="primary"
            @click="importWorkSummaryByCsv"
          >
            Update
          </b-button>
        </div>
      </div>
    </div>
    <Loading :isLoading="isLoading" />
  </div>
</template>

<script lang="ts">
import _ from 'lodash'
import moment from 'moment'
import Vue from 'vue'
// @ts-expect-error 型チェック不要部分
import FileSaver from 'file-saver'
import Loading from '@/components/parts/modal/Loading.vue'
import FileInput from '@/components/parts/inputItem/FileInput.vue'
import ErrorCsv from '@/components/parts/ErrorCsv.vue'
import { readCsv, countRecords } from '@/utils/csv'
import TimesheetWorkSummaryTable from '@/components/parts/timesheet/TimesheetWorkSummaryTable.vue'

export default Vue.extend({
  components: {
    Loading,
    FileInput,
    ErrorCsv,
    TimesheetWorkSummaryTable,
  },

  data: (): {
    selectedYear: number
    selectedMonth: number
    isLoading: boolean
    selectedCsv: File|null
    error: string[]|string|Object|null
    isConfirmed: boolean
  } => ({
    selectedYear: Number(moment().format('YYYY')),
    selectedMonth: Number(moment().format('M')),
    isLoading: false,
    selectedCsv: null,
    error: null,
    isConfirmed: false,
  }),
  computed: {
    years(): number[] {
      const start = 2021
      const end = Number(moment().format('YYYY'))
      // 開始年〜終了年
      return _.range(start, end + 1)
    },
    months(): { month: number, label: string }[] {
      // 1月から12月
      return _.range(1, 12 + 1)
        .map((month) => ({
          month,
          label: moment(month, 'M').format('MMM'),
        }))
    },
    diffs() {
      return this.$store.state.timesheets.WorkSummaryDiffs
    },
  },

  methods: {
    /**
     * 勤務集計をダウンロードする
     */
    async download() {
      if (this.isLoading) return
      this.isLoading = true

      try {
        const { data } = await this.$store.dispatch('downloadWorkingSummary', {
          year: this.selectedYear,
          month: this.selectedMonth,
        })
        const blob = new Blob([data], { type: 'text/plain;charset=utf-8' })
        const ym = `${this.selectedYear}${this.selectedMonth}`
        FileSaver.saveAs(blob, `work_summary-${moment(ym, 'YYYYM').format('YYYYMM')}.csv`)

        this.isLoading = false
        this.$bvModal.msgBoxOk('Success!')
      } catch (error: any) {
        this.isLoading = false
        const message = _.has(error, 'response') ? error.response.data.message : error.message
        this.$bvModal.msgBoxOk(`Failed. ${message}`)
      }
    },

    /**
     * インポートするCSVのチェック
     */
    async confirmUploadCsv() {
      if (this.isLoading) return

      if (!this.selectedCsv) {
        this.error = 'required csv file.'
        return
      }
      this.error = null

      try {
        const reader = await readCsv(this.selectedCsv)
        const recordsCount = countRecords(reader);
        const isOk = await this.$bvModal.msgBoxConfirm(`Import ${recordsCount} Work summaries. Is it OK?`)

        if (isOk) {
          (this as any).confirmWorkSummaryDifference()
        }
      } catch (error) {
        this.error = 'selected file has error. please select file again.'
        this.selectedCsv = null
      }
    },

    /**
     * 勤務集計一覧の差分を取得
     */
    confirmWorkSummaryDifference() {
      this.isLoading = true
      this.$store.dispatch('confirmWorkSummaryDifference', {
        csv: this.selectedCsv,
      })
        .then(() => {
          this.isConfirmed = true
        })
        .catch((error) => {
          this.error = error
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    /**
     * 勤務集計一覧更新
     */
    importWorkSummaryByCsv() {
      this.isLoading = true
      this.$store.dispatch('importWorkSummaryByCsv', {
        csv: this.selectedCsv,
      })
        .then(() => {
          this.$bvModal.msgBoxOk('Success!')
          this.isConfirmed = false
          this.selectedCsv = null
        })
        .finally(() => {
          this.isLoading = false
        })
    },
  },
})
</script>

<style scoped>
.common__main-content {
  min-height: initial;
}
</style>
